import { Group, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getPaymentAndDetails } from "api/payment.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
} from "common/constants";
import { formatDate, formatNZD } from "common/utils";
import React, { useEffect, useState } from "react";
import type {
  IPreviewPageProps,
  PaymentDataEntryForm,
} from "types/model.types";

const AdminPaymentPreview: React.FC<IPreviewPageProps> = ({ id }) => {
  const [exporting, setExporting] = useState(false);
  const [initialData, setInitialData] = useState<PaymentDataEntryForm | null>(
    null,
  );

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await safeApiCall(getPaymentAndDetails(id));
        if (mounted) setInitialData(response ?? null);
      } catch (error) {
        logError(error);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <>
      <div className="sub-title">
        *** Payment Batch #{initialData?.summary.id} ***
      </div>
      <table className="print-table padTop">
        <tbody>
          <tr>
            <td>
              <Group justify="space-between" mb="md" grow wrap="nowrap">
                <Text size="md" fw={700}>
                  Date Ordered:
                </Text>
                <Text size="md" fw={700} c="dimmed">
                  {formatDate(
                    initialData?.summary.createdOn ?? new Date().toDateString(),
                  )}
                </Text>
              </Group>
            </td>
            <td>
              <Stack gap={3}>
                <Group justify="flex-end">
                  <Text size="md" fw={700}>
                    Entered:
                  </Text>
                  <Text
                    size="md"
                    fw={700}
                    c="dimmed"
                    style={{ width: "90px", textAlign: "right" }}
                  >
                    {formatNZD(initialData?.summary.entered ?? 0)}
                  </Text>
                </Group>
                <Group justify="flex-end">
                  <Text size="md" fw={700}>
                    Expected:
                  </Text>
                  <Text
                    size="md"
                    fw={700}
                    c="dimmed"
                    style={{ width: "90px", textAlign: "right" }}
                  >
                    {formatNZD(initialData?.summary.expected ?? 0)}
                  </Text>
                </Group>
                <Group justify="flex-end">
                  <Text size="md" fw={700}>
                    Deductions
                  </Text>
                  <Text
                    size="md"
                    fw={700}
                    c="dimmed"
                    style={{
                      textAlign: "right",
                      width: "90px",
                    }}
                  >
                    {formatNZD(initialData?.summary.coOpFees ?? 0)}
                  </Text>
                </Group>
              </Stack>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="spacer padTop padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Invoice #</th>
            <th className="nowrap">Debtor</th>
            <th className="description">Name</th>
            <th className="money">Amount</th>
          </tr>
        </thead>

        <tbody>
          {initialData?.entries
            .slice()
            .sort((a, b) => Number(a.invoiceId) - Number(b.invoiceId))
            .map((item) => (
              <tr key={item.id}>
                <td className="nowrap">{item.invoiceId}</td>
                <td className="nowrap">{item.debtorId}</td>
                <td className="description">{item.debtorName ?? 0}</td>
                <td className="money">{formatNZD(item.amount)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  );
};

export default AdminPaymentPreview;
